import {
  Autocomplete,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LanguagesCheckbox from "../components/LanguagesCheckbox";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";
import { useMeasurement } from "../hooks/useMeasurement";
import DraggableList from "../object/DraggableList";
import { getAttributeIcon, getDefaultUnit } from '../utils/lib';
import AttributeBox from "./AttributeBox";

/**
 * Implements the attribute-management for a product.
 * Attribute-data can be accesses from a parent via the ref,
 * because an imperative-handle has been configured
 */
const ProductAttributes = forwardRef(({ isStatic, initialAttributes, region, requiredAttributes, valueRequiredAttributes }, ref) => {
  const [selectedAttribute, setSelectedAttribute] = useState({ key: '', type: 'TEXT' });
  const [attributes, setAttributes] = useState(initialAttributes);
  // list that holds all attribute-suggestions from the server
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [attributesOpen, setAttributesOpen] = useState(false);
  const attributesLoading = false && attributeOptions.length === 0;
  // object that holds references to all attribute-react-elements
  const childRefs = useRef({});
  const attributeId = useRef(-1);
  // if attributes should be displayed in every language
  const [allLanguages, setAllLanguages] = useState(false);

  // locale for language
  const { locale } = useLanguage();
  const intl = useIntl();
  const api = useApi();

  const { type: measurementType } = useMeasurement();

  /**
   * Fetches attribute-options from the server.
   */
  useEffect(() => {
    api.get(`/attribute/all?published=true&all=true&allLanguages=${allLanguages}&lang=${locale}&addInfo=true&country=${region}`)
      .then(response => {
        setAttributeOptions([...response.data]);
      })
      .catch(error => {
        console.log(error);
      });
  }, [allLanguages, locale, region]);

  /**
   * Update initial attributes.
   */
  useEffect(() => {
    setAttributes(initialAttributes);
  }, [initialAttributes]);

  /**
   * The methods that are given to the outside-world through the ref.
   */
  useImperativeHandle(ref, () => ({
    addAttributesFromObject,
    getAllAttributeData,
    getAttributeOptions: () => attributeOptions
  }));

  /**
   * Fetches all attribute-data from the AttributBox-elements that are children.
   * @returns 
   */
  const getAllAttributeData = () => {
    return attributes.map(at => childRefs.current[at.id]?.getData()).filter(d => d);
  };

  /**
   * Sets the default attribute-value of the specified attribute according to its type.
   * If a default-value is set on the attribute, uses the default-value.
   * @param {*} attribute 
   */
  const setAttributeDefaultValue = (attribute) => {
    attribute.value = null;
    if (!attribute.attribute.validation[attribute.country]) {
      attribute.attribute.validation[attribute.country] = {};
    }
    const defaults = attribute.attribute.defaults[attribute.country] || {};
    const validation = attribute.attribute.validation[attribute.country] || {};
    switch (attribute.attribute.type) {
      case 'BOOL':
        attribute.value = defaults?.value ?? '';
        break;
      case 'TEXT':
        attribute.value = defaults?.value != null ? defaults.value : '';
        break;
      case 'NUMBER':
        if (attribute.attribute.unitFamily) {
          // unitFamily is set, must also set unit
          attribute.unit = validation?.unit || getDefaultUnit(attribute.attribute.unitFamily, measurementType);
        }
        if (defaults?.value != null) {
          attribute.value = defaults.value;
        } else {
          attribute.value = null;
        }
        console.log(attribute.value);
        break;
      case 'SELECTION':
        attribute.value = defaults?.value !== undefined ? defaults.value : null;
        break;
    }
  };

  /**
   * Handle, when the key of the attribute changes.
   * @param {*} value 
   */
  const handleAttributeKeyChange = (value) => {
    setSelectedAttribute(attribute => {
      let obj = {};
      if (value.key) {
        // the attribute is predefined (fetched from the server)
        obj = { ...value };
      } else {
        // the attribute is not defined yet, set type to 'TEXT' as default
        obj = { type: 'TEXT', key: value };
      }
      return obj;
    });
  };

  /**
   * Reorders the attributes by moving the attribute at position oldPos to newPos.
   * newPos may be -1 and then indicates that the attribute should be pushed to the end of the array.
   * @param {*} oldPos 
   * @param {*} newPos 
   */
  const reorderAttributes = (oldPos, newPos) => {
    setAttributes(old => {
      const oldAttributes = getAllAttributeData();
      const [removedEl, _] = oldAttributes.splice(oldPos, 1);
      if (newPos === -1) {
        oldAttributes.push(removedEl);
      } else if (oldPos < newPos) {
        oldAttributes.splice(newPos - 1, 0, removedEl);
      } else {
        oldAttributes.splice(newPos, 0, removedEl);
      }
      return [...oldAttributes];
    });
  }

  /**
   * Adds the specified attribute to the attribute-list of this product.
   * @returns 
   */
  const addAttribute = (selectedAttribute) => {
    if (!selectedAttribute.key || !selectedAttribute.type) return;
    // object that holds the new attribute to add
    let obj;
    // check if there is at least one attribute with the exact same name
    const atMatch = attributeOptions.filter(at => at.key === selectedAttribute.key);
    if (!selectedAttribute.id && atMatch.length > 0) {
      // choose first option of possible attributes
      obj = { ...atMatch[0] };
    } else {
      obj = { ...selectedAttribute };
    }

    // check if the attribute is already associated with the object
    // check for same id
    const ids = attributes.map(at => at.attribute.id);
    if (ids.filter(id => id === obj.id).length > 0) {
      return;
    }
    // check for same key-type-pair
    if (attributes.filter(at => at.attribute.key === obj.key && at.attribute.type === obj.type).length > 0) {
      return;
    }

    if (obj.id == null) {
      // no id is set, create new attribute
      obj.id = getNextAttributeId();
      obj.defaults = {};
      obj.validation = {};
    }

    // the attribute-instance-object that includes the values
    const attribute = { attribute: obj };
    attribute.id = getNextAttributeId();
    // the currently selected region is set for the attribute-instance
    attribute.country = region;
    setAttributeDefaultValue(attribute);
    // add attribute-instance
    console.log(attribute);
    setAttributes(old => {
      return [...old, attribute];
    });
    // reset selected attribute
    setSelectedAttribute({ key: '', type: 'TEXT' });
  };

  /**
   * Remove the specified attribute.
   * @param {*} id 
   * @returns 
   */
  const removeAttribute = (id) => () => {
    const at = childRefs.current[id].getData();
    // remove from attributes
    setAttributes(old => {
      return [...old.filter(a => a.id != id)];
    })
    // remove from childRefs
    delete childRefs.current[id];
  };

  /**
   * Adds all attributes that have not already been set on this product.
   */
  const addAttributesFromObject = (attributes) => {
    // possible attribute-instance-objects to add
    const attributeInstances = attributes.map(at => {
      at = { id: getNextAttributeId(), value: null, attribute: { ...at.attribute }, country: region };
      // set the attributes default value
      setAttributeDefaultValue(at);
      return at;
    });
    // add all possible attribute-instances whose attributes have not been added yet to the product
    setAttributes(old => {
      old = [...old];
      const listIds = old.map(at => at.attribute.id);
      // object that holds new attribute-instances
      const arr = [];
      attributeInstances.forEach(at => {
        if (!listIds.includes(at.attribute.id)) {
          // if not included yet, add to object
          arr.push(at)
        }
      });
      // add object to attributes
      return [...old, ...arr];
    });
  };

  const getNextAttributeId = () => {
    return attributeId.current--;
  };

  /**
   * List of all attribute-instances as AttributeBox-elements that are associated with product.
   */
  const attributeList = useMemo(() => {
    return attributes.map((at) => {
      return (
        <AttributeBox
          isStatic={isStatic}
          key={at.id}
          attributeProp={at}
          removeAttribute={removeAttribute(at.id)}
          ref={(el) => (childRefs.current[at.id] = el)}
          required={requiredAttributes[at.attribute.id] ? true : false}
          valueRequired={valueRequiredAttributes[at.attribute.id] ? true : false}
        ></AttributeBox>
      );
    });
  }, [attributes, attributeOptions, isStatic, requiredAttributes, valueRequiredAttributes]);

  const textFieldStyle = {
    width: '90%'
  };

  return (
    <Grid item container spacing={3} alignItems="center" xs={12} >
      <Grid item xs={12}>
        <Typography variant="h6" >
          <FormattedMessage id="EditProduct.attributeHeading" />
        </Typography>
      </Grid>

      {!isStatic && <Grid item xs={12} sm={8} md={5} >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Autocomplete
            freeSolo
            style={{ width: '100%' }}
            value={selectedAttribute}
            inputValue={selectedAttribute.key}
            open={attributesOpen}
            onOpen={() => setAttributesOpen(true)}
            onClose={() => setAttributesOpen(false)}
            onInputChange={(event, value) => {
              // is called every time the visible value changes
              handleAttributeKeyChange(value);
            }}
            onChange={(event, value) => {
              // is called when an option is selected from the dropdown
              if (!value) {
                // in case of clearing the option, the value is null
                handleAttributeKeyChange('');
                return;
              }
              addAttribute(value);
            }}
            onKeyDown={(event, value) => {
              if (event.key === 'Enter') {
                // when enter is pressed, try to add the object
                event.preventDefault();
                // lose focus
                event.target.blur();
                addAttribute(selectedAttribute);
              }
            }}
            options={attributeOptions}
            getOptionLabel={(option) => {
              if (option.key !== undefined) {
                return option.key;
              } else {
                return option;
              }
            }}
            id={`combo-box-key`}
            loading={attributesLoading}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                size="small"
                {...params}
                label={intl.formatMessage({ id: "AttributeBox.key" })}
                sx={textFieldStyle}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {attributesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 50
                }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              let icon = getAttributeIcon(option.type);
              return (
                <li {...props} key={option.id} style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  {option.key}
                  {icon}
                </li>
              )
            }}
          >
          </Autocomplete>
          {!isStatic && <LanguagesCheckbox initialValue={allLanguages} onChange={setAllLanguages} disabled={isStatic} style={{ marginRight: '10px' }} />}
        </div>
      </Grid>}
      {!isStatic && <Grid item xs={12} sm={6} md={3} >
        <Button variant="outlined" onClick={() => addAttribute(selectedAttribute)} ><FormattedMessage id="EditProduct.addAttribute" /></Button>
      </Grid>}
      <Grid item xs={12}>
        <DraggableList reorderList={reorderAttributes} isStatic={isStatic} >
          {attributeList}
        </DraggableList>
      </Grid>
    </Grid>
  )
});

export default ProductAttributes;